/**
 * External dependencies
 */
import React, { useState, useEffect } from "react";
import { Box, Text, Divider } from "@towersystems/towersystems-design-system";
import page from "page";
import { connect } from "react-redux";
import { Table } from "antd";
import {
  map,
  get,
  filter,
  lowerCase,
  includes,
  find,
  size,
  omitBy,
  isEmpty,
} from "lodash";
import queryString from "querystring";
import classNames from "classnames";
import { TbFilterSearch } from "react-icons/tb";
import { IoMdClose } from "react-icons/io";
import { useMediaQuery } from "react-responsive";

/**
 * Internal dependencies
 */
import Main from "components/main";
import DocumentHead from "data/document-head";
import PageHeader from "layout/page-header";
import Button from "components/button";
import Navigation from "modules/reports/navigation";
import ReportFilters from "modules/reports/report-filters";
import { getReports } from "state/reports/selectors";
import TagGroup from "components/tag/tag-group";
import Tag from "components/tag";
import DropdownSelect from "components/dropdown-select";
import RenameReportPopover from "blocks/rename-report-popover";
import ReportPreview from "./report-preview";
import { FaCheck } from "react-icons/fa6";
import SwitchReportDropdownSelect from "blocks/switch-report-dropdown-select";
import RowAction from "./row-action";
import { ButtonV2 } from "components/button-v2";
import { FiPlus } from "react-icons/fi";

/**
 * Style dependencies
 */
import "./styles.scss";
import {
  getDashboardLayout,
  getName,
} from "../../../state/dashboard/selectors";

const MyReports = ({ reports, dashboard, dashboardDescription, ...props }) => {
  const isMobile = useMediaQuery({ maxWidth: "1150px" });
  const [query, setQuery] = useState(props.query || "");
  const [filterCustomized, setFilterCustomized] = useState(false);
  const [dataSetFilters, setDataSetFilters] = useState(props.dataSetFilters);
  const [dataTypeFilters, setDataTypeFilters] = useState(props.dataTypeFilters);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportPreviewVisible, setReportPreviewVisible] = useState(false);
  const [isMenuOpen, setShowMenu] = useState(false);

  const toggleMenu = () => {
    setShowMenu(!isMenuOpen);
  };

  useEffect(() => {
    updatePath();
  }, [dataSetFilters, dataTypeFilters, query]);

  const updatePath = () => {
    let currentPath = "/reports/my-reports";
    let q = queryString.stringify(
      omitBy({ dataSetFilters, dataTypeFilters, query }, (v) => isEmpty(v))
    );

    if (q) {
      return page(`${currentPath}?${q}`);
    } else {
      return page(`${currentPath}`);
    }
  };

  const filterReports = (reports) => {
    let filtered = filter(reports, (report) => {
      if (filterCustomized === true) {
        return get(report, "template") === "custom";
      }
      return true;
    });

    filtered = filter(filtered, (report) => {
      return lowerCase(get(report, "name", "")).includes(lowerCase(query));
    });

    // filter the data sets
    filtered = filter(filtered, (report) => {
      return (
        size(dataTypeFilters) === 0 ||
        includes(dataTypeFilters, get(report, "config.dataType"))
      );
    });

    filtered = filter(filtered, (report) => {
      return (
        size(dataSetFilters) === 0 ||
        includes(dataSetFilters, get(report, "config.dataSet"))
      );
    });

    return filtered;
  };

  const handleReportFilterChange = ({ dataSetFilters, dataTypeFilters }) => {
    setDataSetFilters(dataSetFilters);
    setDataTypeFilters(dataTypeFilters);
  };

  // create the config for the table columns
  const columnConfig = [
    {
      key: "name",
      title: "Name",
      render: (a, report) => {
        return (
          <ButtonV2
            onClick={() => page(`/report-builder/${report.id}/edit`)}
            variant="black"
            text={true}
          >
            {report.name}
          </ButtonV2>
        );
      },
      width: "60%",
      sorter: (a, b) => a.name - b.name,
    },
    {
      key: "dashboardkeych",
      title: `Deck (${dashboardDescription})`,
      align: "center",
      render: (a, report) => {
        if (report.inDashboard) {
          return (
            <div className="flex justify-center">
              <FaCheck />
            </div>
          );
        }
      },
    },
    {
      key: "actions",
      title: "",
      align: "center",
      render: (a, report) => <RowAction report={report} />,
    },
  ];

  // formany any data
  const tags = [...dataSetFilters, ...dataTypeFilters];

  const showTags = size(tags) >= 1;

  const tableData = map(filterReports(reports), (report) => {
    return { key: report.id, ...report };
  });

  const activeReport = find(reports, { id: activeReportId });

  return (
    <Box>
      <PageHeader
        title={isMobile ? undefined : "My Views"}
        actions={
          <Box
            width={isMobile ? "full" : "auto"}
            className="flex gap-3 items-center"
          >
            <Box
              style={{
                flexGrow: 1,
              }}
            >
              <input
                className="input input--white"
                type="search"
                placeholder="Search the report library"
                onChange={(e) => setQuery(e.target.value)}
                value={query}
                style={{ width: "100%", marginRight: 15 }}
              />
            </Box>
            <Box>
              <ButtonV2
                variant="primary"
                onClick={() => page("/report-builder/new")}
                leftIcon={<FiPlus size={20} />}
              >
                Create view
              </ButtonV2>
            </Box>
          </Box>
        }
      />
      <DocumentHead title="Views - My Views" />
      <Main
        className={classNames("report__content", isMobile && "mobile-view")}
      >
        <Box
          className={classNames(
            "report__content-left",
            isMenuOpen && "is-open"
          )}
        >
          {isMobile ? (
            <Box
              marginRight="large"
              display="flex"
              justifyContent="flexEnd"
              paddingY="large"
            >
              <Button
                compact
                className="topbar__toggle"
                id="topbar-toggle"
                onClick={toggleMenu}
              >
                <IoMdClose size={20} />
              </Button>
            </Box>
          ) : null}
          <Navigation selected="my-reports" />
          <div className="mt-12">
            <ReportFilters
              defaultDataTypeFilters={dataTypeFilters}
              defaultDataSetFilters={dataSetFilters}
              onChange={handleReportFilterChange}
            />
          </div>
        </Box>
        <Box className="report__content-right">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="spaceBetween"
            marginBottom="large"
          >
            <Box display="flex" alignItems="center">
              {isMobile ? (
                <Box marginRight="large">
                  <Button
                    compact
                    className="topbar__toggle"
                    id="topbar-toggle"
                    onClick={toggleMenu}
                  >
                    <TbFilterSearch size={20} />
                  </Button>
                </Box>
              ) : null}
              <Text size="small">
                Showing <span className="bold">{size(tableData)}</span> results
              </Text>
            </Box>
            <div className="flex gap-3">
              <SwitchReportDropdownSelect />
              <DropdownSelect
                selected="all"
                onChange={(v) => setFilterCustomized(v === "customizable")}
                options={[
                  { key: "all", value: "all", label: "All views" },
                  {
                    key: "customizable",
                    value: "customizable",
                    label: "Only customizable",
                  },
                ]}
              />
            </div>
          </Box>
          {showTags && (
            <Box marginBottom="large">
              <TagGroup>
                {map(tags, (f) => (
                  <Tag key={f} label={f} />
                ))}
              </TagGroup>
            </Box>
          )}
          <Table
            dataSource={tableData}
            columns={columnConfig}
            pagination={{
              position: "bottom",
              pageSize: 20,
              hideOnSinglePage: true,
            }}
          />
        </Box>
      </Main>
      <RenameReportPopover />
      <ReportPreview
        report={activeReport}
        isVisible={reportPreviewVisible}
        close={() => setReportPreviewVisible(false)}
      />
    </Box>
  );
};

export default connect((state) => {
  const dashboardLayout = getDashboardLayout(state);
  return {
    dashboard: dashboardLayout,
    dashboardDescription: getName(state),
    reports: map(getReports(state), (report) => {
      const inDashboard = dashboardLayout?.items?.find(
        (item) => item.widgetId === report.id
      );

      return {
        ...report,
        inDashboard,
      };
    }),
  };
})(MyReports);
